import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Trophy,
  AlertCircle,
  Plus,
  Search,
  Check,
  MoreHorizontal,
  Pencil,
  Trash2
} from 'lucide-react';
import FeaturedLayout from '../layouts/FeaturedLayout';
import FormTorneo from './FormTorneo';
import TorneoCard from './TorneoCard';
import { getResumenDeTorneos } from '../../lib/services/participaciones';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../hooks/useAuth';
import { useOrganization } from '../../contexts/OrganizationContext';
import {
  Card,
  CardContent,
  Alert,
  AlertTitle,
  AlertDescription,
  Button,
  Input,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent
} from '../ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../ui/dropdown-menu';
import {
  getAllTorneosWithInscripciones,
  createTorneo,
  updateTorneo,
  deleteTorneo,
  getStaffGroups,
  getAllGroups
} from '../../lib/supabase';
import { cn, fixMojibake } from '../../lib/utils';
import { clubCapturaEsteTorneo } from '../../lib/torneoOrigen';
import {
  etapaDeTorneo,
  chipsDeEtapa,
  esHistorial
} from './torneoStatus';


// Pipeline de busqueda/filtros aplicado dentro de un bucket (Activos o Historial),
// pura para poder correrla una vez por bucket sin duplicar JSX.
const buildBucketData = (list, { searchTerm, tab, statusFilter }) => {
  const term = searchTerm.trim().toLowerCase();
  const baseTorneos = list.filter((torneo) => {
    const matchesSearch = term
      ? fixMojibake(torneo.nombre || '').toLowerCase().includes(term) ||
        fixMojibake(torneo.descripcion || '').toLowerCase().includes(term) ||
        fixMojibake(torneo.sede || '').toLowerCase().includes(term)
      : true;
    const matchesTab = tab === 'todos' ? true : torneo.esMio;
    return matchesSearch && matchesTab;
  });

  // Se filtra por ETAPA, no por `estatus`. `estatus` no es otro estado: es un espejo
  // grueso de la etapa que la base mantiene sincronizado, y filtrar por el metia bajo
  // "Proximo" a un torneo en borrador, uno en inscripciones y uno en programacion --
  // tres momentos distintos para quien organiza, que es justo lo que quiere separar.
  const statusChips = chipsDeEtapa(baseTorneos, statusFilter);

  const filteredTorneos = statusFilter === 'todos'
    ? baseTorneos
    : baseTorneos.filter((torneo) => etapaDeTorneo(torneo) === statusFilter);

  return {
    baseTorneos,
    statusChips,
    filteredTorneos,
    propios: filteredTorneos.filter((t) => t.esOrganizador),
    invitados: filteredTorneos.filter((t) => !t.esOrganizador),
    hayTorneosMios: list.some((t) => t.esMio)
  };
};

const Torneos = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { org } = useOrganization();
  const { canManageTorneos } = usePermissions();
  const { confirm, alert, ConfirmDialog } = useConfirmDialog();

  const [torneos, setTorneos] = useState([]);
  // El proximo juego y el record de cada grupo: es lo que la tarjeta contesta.
  const [resumen, setResumen] = useState({ proximos: new Map(), inscripciones: new Map() });
  const [misGrupoIds, setMisGrupoIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [tab, setTab] = useState('todos'); // todos | mios
  const [vista, setVista] = useState('activos'); // activos | historial
  const [showForm, setShowForm] = useState(false);
  const [editingTorneo, setEditingTorneo] = useState(null);
  const [saving, setSaving] = useState(false);

  // Dos vueltas al servidor en serie: la lista, y con sus ids el resumen (proximo juego e
  // inscripciones por torneo). La pantalla se quedaba en "Cargando torneos..." hasta la
  // segunda vuelta aunque la lista ya estaba (el dueno, 2026-09-08). Ahora la lista se
  // pinta en cuanto llega y el resumen rellena las tarjetas despues; si falla, no tumba
  // la lista.
  const loadTorneos = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    let ids = [];
    try {
      const { data, error } = await getAllTorneosWithInscripciones();
      if (error) throw error;
      setTorneos(data || []);
      ids = (data || []).map((t) => t.id).filter(Boolean);
    } catch (err) {
      console.error('Error cargando torneos:', err);
      setFetchError('No pudimos recuperar los torneos. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
    if (ids.length === 0) return;
    try {
      const { data: res } = await getResumenDeTorneos(ids);
      if (res) setResumen(res);
    } catch (err) {
      console.error('Error cargando el resumen de torneos:', err);
    }
  }, []);

  const loadMisGrupos = useCallback(async () => {
    if (!user) return;
    try {
      const fetcher = canManageTorneos ? getAllGroups : getStaffGroups;
      const { data } = await fetcher(user?.id);
      const ids = new Set((data || []).map((g) => g.id));
      setMisGrupoIds(ids);
    } catch (err) {
      console.error('Error cargando mis grupos:', err);
    }
  }, [user, canManageTorneos]);

  useEffect(() => {
    loadTorneos();
    loadMisGrupos();
  }, [loadTorneos, loadMisGrupos]);

  const enrichedTorneos = useMemo(() => {
    return (torneos || []).map((t) => {
      const inscripciones = (t.torneos_inscripciones || []).filter((i) => i.activo !== false);
      const inscripcionesMias = inscripciones.filter((i) => misGrupoIds.has(i.grupo_id));
      const partidosJugados = inscripciones.reduce((acc, i) => acc + (i.partidos_jugados || 0), 0);
      // El club ORGANIZA el torneo si su org es la organizadora; si no, solo PARTICIPA
      // (o lo ve): esos van a la vista de participante (rol/posiciones/eliminatoria).
      // La misma funcion que decide los botones de captura en las demas pantallas:
      // esta lista tenia su propia comparacion, y un torneo sin organizadora (dato
      // viejo) se leia "interno" en la agenda y "no es mio" aqui.
      const esOrganizador = clubCapturaEsteTorneo(t, org?.id);
      return {
        ...t,
        inscripcionesActivas: inscripciones,
        esMio: inscripcionesMias.length > 0,
        esOrganizador,
        totalInscripciones: inscripciones.length,
        partidosJugados
      };
    })
    // Esta pantalla es la de los torneos INTERNOS. Los de la red viven en MYKLUBB
    // Torneos, que desde 20260902 los lista por inscripcion y ya se sostiene solo.
    // El filtro va aqui y no en la seccion para que los conteos del encabezado y las
    // pestanas Activos/Historial tampoco cuenten los ajenos.
    .filter((t) => t.esOrganizador);
  }, [torneos, misGrupoIds, org?.id]);

  // Activos vs Historial: historial = archivado o en etapa terminal (finalizado/cancelado).
  const { activosEnriched, historialEnriched } = useMemo(() => ({
    activosEnriched: enrichedTorneos.filter((t) => !esHistorial(t)),
    historialEnriched: enrichedTorneos.filter((t) => esHistorial(t)),
  }), [enrichedTorneos]);

  // Busqueda/filtros se aplican dentro de cada bucket (Activos / Historial) por separado.
  const activosData = useMemo(
    () => buildBucketData(activosEnriched, { searchTerm, tab, statusFilter }),
    [activosEnriched, searchTerm, tab, statusFilter]
  );
  const historialData = useMemo(
    () => buildBucketData(historialEnriched, { searchTerm, tab, statusFilter }),
    [historialEnriched, searchTerm, tab, statusFilter]
  );
  const currentData = vista === 'historial' ? historialData : activosData;

  const handleOpenForm = async (torneo = null) => {
    if (!canManageTorneos) {
      await alert('Acceso restringido', 'No tienes permisos para crear o editar torneos.');
      return;
    }
    setEditingTorneo(torneo);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setEditingTorneo(null);
    setShowForm(false);
  };

  const handleSubmitTorneo = async (torneoData) => {
    try {
      setSaving(true);
      if (editingTorneo) {
        const { error } = await updateTorneo(editingTorneo.id, torneoData);
        if (error) throw error;
        await alert('Listo', 'Torneo actualizado.');
      } else {
        const { data, error } = await createTorneo(torneoData);
        if (error) throw error;
        await alert('Torneo creado', 'Ahora puedes inscribir grupos al torneo.');
        await loadTorneos();
        handleCloseForm();
        // Navegar al final: antes se navegaba primero y la recarga de la lista nunca
        // llegaba a verse -- al volver, el torneo recien creado no aparecia.
        if (data?.id) { navigate(`/torneos/${data.id}`); return; }
      }
      await loadTorneos();
      handleCloseForm();
    } catch (error) {
      console.error('Error guardando torneo:', error);
      await alert('Error', 'No se pudo guardar el torneo. Intenta nuevamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTorneo = async (torneo) => {
    const confirmed = await confirm(
      'Eliminar torneo',
      `¿Eliminar "${fixMojibake(torneo.nombre)}"? Si tiene partidos relacionados, no se podrá eliminar.`,
      { destructive: true }
    );
    if (!confirmed) return;
    try {
      const { error } = await deleteTorneo(torneo.id);
      if (error) throw error;
      await alert('Listo', 'Torneo eliminado correctamente.');
      await loadTorneos();
    } catch (error) {
      console.error('Error eliminando torneo:', error);
      await alert('Error', 'No se pudo eliminar el torneo.');
    }
  };

  const renderGrid = (lista) => (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
      {lista.map((torneo) => (
        <TorneoCard
          key={torneo.id}
          torneo={torneo}
          modo={torneo.esOrganizador ? 'organizas' : 'participas'}
          proximo={resumen.proximos.get(torneo.id) || null}
          inscripciones={resumen.inscripciones.get(torneo.id) || []}
          onOpen={() => navigate(torneo.esOrganizador ? `/torneos/${torneo.id}` : `/torneos-myklubb/${torneo.id}`)}
          acciones={canManageTorneos && torneo.esOrganizador ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 shrink-0 p-0" title="Más acciones">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleOpenForm(torneo)}>
                  <Pencil className="h-4 w-4" /> Editar torneo
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleDeleteTorneo(torneo)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4" /> Eliminar torneo
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        />
      ))}
    </div>
  );

  const renderBucketContent = (data, bucketKey) => {
    if (data.filteredTorneos.length === 0) {
      return (
        <Card>
          <CardContent className="py-12 text-center">
            <Trophy className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
            <p className="text-muted-foreground mb-2">
              {enrichedTorneos.length === 0
                ? 'Todavía no hay torneos'
                : bucketKey === 'historial'
                  ? 'No hay torneos en el historial'
                  : 'No encontramos torneos con esos criterios'}
            </p>
            {enrichedTorneos.length === 0 ? (
              canManageTorneos && (
                <Button size="sm" className="mt-2" onClick={() => handleOpenForm()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Crear el primero
                </Button>
              )
            ) : (
              <p className="text-xs text-muted-foreground">
                {bucketKey === 'historial'
                  ? 'Los torneos finalizados, cancelados o archivados aparecerán aquí.'
                  : 'Prueba con otro término de búsqueda o quita los filtros.'}
              </p>
            )}
          </CardContent>
        </Card>
      );
    }
    return (
      <div className="space-y-6">
        <section className="space-y-2">
          {data.propios.length > 0 ? renderGrid(data.propios) : (
            <p className="py-2 text-xs text-muted-foreground">
              Aún no llevas ningún torneo aquí. Usa "Nuevo torneo" para registrar la
              competencia en la que juega tu club.
            </p>
          )}
        </section>
      </div>
    );
  };

  return (
    <FeaturedLayout
      title="Torneos"
      subtitle="Gestiona competencias, inscripciones y partidos."
      refreshAction={loadTorneos}
    >
      <div className="space-y-3">
        {/* Toolbar (mismo patron que Temporadas/Grupos: una fila, labels colapsan en movil) */}
        <div className="flex items-center gap-2 flex-nowrap">
          <div className="flex-1 min-w-0 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar torneo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground hidden sm:block whitespace-nowrap">
              <span className="font-medium text-foreground">{currentData.filteredTorneos.length}</span>{' '}
              {currentData.filteredTorneos.length === 1 ? 'torneo' : 'torneos'}
            </span>
            {canManageTorneos && (
              <Button onClick={() => handleOpenForm()} className="h-10" aria-label="Nuevo torneo" title="Nuevo torneo">
                <Plus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Nuevo torneo</span>
              </Button>
            )}
          </div>
        </div>

        <Tabs value={vista} onValueChange={setVista}>
          <TabsList>
            <TabsTrigger value="activos">Activos ({activosEnriched.length})</TabsTrigger>
            <TabsTrigger value="historial">Historial ({historialEnriched.length})</TabsTrigger>
          </TabsList>

          {/* Filtros: aparecen solo cuando hay algo que filtrar, dentro del bucket activo */}
          {(currentData.statusChips.length > 0 || currentData.hayTorneosMios) && (
            <div className="flex flex-wrap items-center gap-1.5 mt-3">
              {currentData.statusChips.map((chip) => {
                const active = statusFilter === chip.value;
                return (
                  <Button
                    key={chip.value}
                    variant={active ? 'default' : 'outline'}
                    size="sm"
                    className="h-8 rounded-full px-3 text-xs font-normal"
                    onClick={() => setStatusFilter(chip.value)}
                  >
                    {chip.label}
                    <span className={cn('ml-1.5 tabular-nums', !active && 'text-muted-foreground')}>
                      {chip.count}
                    </span>
                  </Button>
                );
              })}
              {currentData.hayTorneosMios && (
                <>
                  {currentData.statusChips.length > 0 && <span className="mx-1 h-5 w-px bg-border" />}
                  <Button
                    variant={tab === 'mios' ? 'default' : 'outline'}
                    size="sm"
                    className="h-8 rounded-full px-3 text-xs font-normal"
                    onClick={() => setTab(tab === 'mios' ? 'todos' : 'mios')}
                  >
                    {tab === 'mios' && <Check className="h-3 w-3 mr-1.5" />}
                    Con mis grupos
                  </Button>
                </>
              )}
            </div>
          )}

          {fetchError && (
            <Alert variant="destructive" className="mt-3">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error al cargar torneos</AlertTitle>
              <AlertDescription>{fetchError}</AlertDescription>
            </Alert>
          )}

          {loading ? (
            <p className="py-12 text-center text-sm text-muted-foreground">Cargando torneos...</p>
          ) : (
            <>
              <TabsContent value="activos">
                {renderBucketContent(activosData, 'activos')}
              </TabsContent>
              <TabsContent value="historial">
                {renderBucketContent(historialData, 'historial')}
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>

      <FormTorneo
          open={showForm}
          onOpenChange={(v) => { if (!v) handleCloseForm(); }}
          torneo={editingTorneo}
          onSubmit={handleSubmitTorneo}
          onCancel={handleCloseForm}
          saving={saving}
        />

      <ConfirmDialog />
    </FeaturedLayout>
  );
};


export default Torneos;
